import { Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { EnvService } from '@core-mkt/services/env/env.service';
import { log, SEVERITY } from '../../../shared/lib/logger';
import type { BlogQueryResult } from './page-data-response';

/**
 * Resolver for fetching paginated blog entries with filtering support.
 * Handles category and state filtering via query parameters.
 */
@Injectable()
export class BlogPageDataResolver {
  constructor(private env: EnvService) {}

  /**
   * Fetches paginated blog entries with optional category and state filters.
   *
   * @param route - The activated route snapshot containing query parameters
   * @param site - The site identifier for the GraphQL query
   * @param craftData - The existing craft data object to append blog entries to
   * @returns Promise that resolves when blog data has been fetched and added to craftData
   */
  async fetchBlogEntries(
    route: ActivatedRouteSnapshot,
    site: string,
    craftData: any,
  ): Promise<void> {
    // Get the page number from query params (default to 1)
    const pageParam = route.queryParams['page'];
    const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
    const limit = 10;
    const offset = (currentPage - 1) * limit;

    // Get the category filter from query params (pass null if missing)
    const catParam = route.queryParams['cat'];
    const blogCategories = catParam ? [parseInt(catParam, 10)] : null;

    // Get the state filter from query params (pass slug string, not ID)
    const stateParam = route.queryParams['state'] as string | undefined;
    const blogStateCategory = stateParam ? [stateParam] : null;

    // Import Apollo dependencies dynamically
    const { EntryListingsBlogQuery } = await import(
      '@core-mkt/shared/components/entry-listings/entry-listings/entry-listings-query-fragment'
    );
    const { ApolloClient, createHttpLink, InMemoryCache } = await import('@apollo/client/core');
    const gql = (await import('graphql-tag')).default;

    const httpLink = createHttpLink({
      uri: this.env.get.blogCraftApiUrl,
    });

    const apolloClient = new ApolloClient({
      link: httpLink,
      cache: new InMemoryCache(),
    });

    try {
      const blogResult = await apolloClient.query<BlogQueryResult>({
        query: gql`
          ${EntryListingsBlogQuery.query}
        `,
        variables: {
          site: site.toLowerCase(),
          limit: limit,
          offset: offset,
          blogCategories: blogCategories,
          blogStateCategory: blogStateCategory,
        },
        fetchPolicy: 'no-cache',
      });

      // Add the paginated blog entries to craftData
      if (blogResult.data?.entryData) {
        craftData['pageBlogEntries'] = blogResult.data.entryData;
        // Also store the total count for pagination
        craftData['pageBlogEntriesCount'] = blogResult.data.entryCount;
        // Store the categories for the dropdown filter
        if (blogResult.data?.blogCategories) {
          craftData['blogCategories'] = blogResult.data.blogCategories;
        }
        if (blogResult.data?.blogStateCategory) {
          craftData['blogStateCategories'] = blogResult.data.blogStateCategory;
        }
      }
    } catch (error) {
      log({
        severity: SEVERITY.ERROR,
        code: 40404,
        message: `Failed to fetch paginated blog entries: ${error.message}`,
        brandId: site,
        tags: ['BlogPageDataResolver.fetchBlogEntries'],
      });
      throw error;
    }
  }

  /**
   * Checks if the page has an EntryListings component that needs blog data.
   *
   * @param craftData - The craft data object containing page components
   * @returns boolean indicating if blog entries should be fetched
   */
  shouldFetchBlogEntries(craftData: any): boolean {
    if (!craftData?.entryData?.baseComponents) {
      return false;
    }

    return craftData.entryData.baseComponents.some(
      (component: any) => component?.typeHandle === 'entryListings',
    );
  }
}
